import { FLAG_KEYS } from "../constants/feature-flag-keys";
import { parseAppConfigDeployment } from "../schema/app-config-deployment";
import type { IAppConfigResponse, TWireAppConfig } from "../types/app-config";

// NOTE: Translates the obfuscated /app-config wire shape back to the readable
// IAppConfigResponse consumed by hooks and components. Mirrors the server's
// to-wire-format.ts inversely — fields absent on the wire stay absent here
// (consumers treat missing keys as disabled).
export const fromWireFormat = (wire: TWireAppConfig): IAppConfigResponse => {
  const result: IAppConfigResponse = {
    isBillingDisabled: wire[FLAG_KEYS.isBillingDisabled],
    maxFileUploadCount: wire[FLAG_KEYS.maxFileUploadCount],
  };

  if (wire[FLAG_KEYS.isTelegramBotEnabled]) {
    result.isTelegramBotEnabled = true;
  }
  if (wire[FLAG_KEYS.isWhatsappBotEnabled]) {
    result.isWhatsappBotEnabled = true;
  }
  // NOTE: NSAI-6224 — the soft-launch twin. The server sends this INSTEAD of the
  // key above, so leaving it out of the decoder would make a soft-launched
  // channel look permanently disabled to every client.
  if (wire[FLAG_KEYS.isWhatsappBotEnabledFF]) {
    result.isWhatsappBotEnabledFF = true;
  }
  if (wire[FLAG_KEYS.isMatrixBotEnabled]) {
    result.isMatrixBotEnabled = true;
  }
  if (wire[FLAG_KEYS.isAudioTranscriptionEnabled]) {
    result.isAudioTranscriptionEnabled = true;
  }
  if (wire[FLAG_KEYS.isExternalServiceForAudioTranscription]) {
    result.isExternalServiceForAudioTranscription = true;
  }
  if (wire[FLAG_KEYS.isKonturTalkEnabled]) {
    result.isKonturTalkEnabled = true;
  }
  if (wire[FLAG_KEYS.isYandexTelemostEnabled]) {
    result.isYandexTelemostEnabled = true;
  }
  if (wire[FLAG_KEYS.isSaluteJazzEnabled]) {
    result.isSaluteJazzEnabled = true;
  }
  if (wire[FLAG_KEYS.isFinancialMaskingFeatureEnabled]) {
    result.isFinancialMaskingFeatureEnabled = true;
  }
  if (wire[FLAG_KEYS.isPiiCustomEntitiesFeatureEnabled]) {
    result.isPiiCustomEntitiesFeatureEnabled = true;
  }
  if (wire[FLAG_KEYS.isPiiAdvancedRulesFeatureEnabled]) {
    result.isPiiAdvancedRulesFeatureEnabled = true;
  }
  if (wire[FLAG_KEYS.isPiiIntentRoutingFeatureEnabled]) {
    result.isPiiIntentRoutingFeatureEnabled = true;
  }
  if (wire[FLAG_KEYS.isAgentsEnabled]) {
    result.isAgentsEnabled = true;
  }
  if (wire[FLAG_KEYS.isCustomIntegrationEnabled]) {
    result.isCustomIntegrationEnabled = true;
  }
  if (wire[FLAG_KEYS.isAnonymizationLabEnabled]) {
    result.isAnonymizationLabEnabled = true;
  }
  if (wire[FLAG_KEYS.isAnonymizationLabEnabledFF]) {
    result.isAnonymizationLabEnabledFF = true;
  }
  if (wire[FLAG_KEYS.isDocRecognitionEnabled]) {
    result.isDocRecognitionEnabled = true;
  }
  if (wire[FLAG_KEYS.isBrandedPresentationsEnabled]) {
    result.isBrandedPresentationsEnabled = true;
  }
  if (wire[FLAG_KEYS.isTokenUsageEnabled]) {
    result.isTokenUsageEnabled = true;
  }
  if (wire[FLAG_KEYS.isTokenCostUsageEnabled]) {
    result.isTokenCostUsageEnabled = true;
  }
  if (wire[FLAG_KEYS.isTokenLimitsEnabled]) {
    result.isTokenLimitsEnabled = true;
  }
  if (wire[FLAG_KEYS.isSyncatorApiEnabled]) {
    result.isSyncatorApiEnabled = true;
  }
  if (wire[FLAG_KEYS.isSkillsEnabled]) {
    result.isSkillsEnabled = true;
  }
  // NOTE: NSAI-3309 — decoded now that the server sends it. Absent stays absent
  // rather than becoming `false`, so a client that reads it still fails closed.
  if (wire[FLAG_KEYS.isSkillScriptExecutionAvailable]) {
    result.isSkillScriptExecutionAvailable = true;
  }
  if (wire[FLAG_KEYS.isAdminPanelEnabled]) {
    result.isAdminPanelEnabled = true;
  }
  if (wire[FLAG_KEYS.isZipArchiveUploadEnabled]) {
    result.isZipArchiveUploadEnabled = true;
  }
  if (wire[FLAG_KEYS.isUserMemoryEnabled]) {
    result.isUserMemoryEnabled = true;
  }

  const jitsiDomains = wire[FLAG_KEYS.jitsiCustomDomains];
  if (jitsiDomains && jitsiDomains.length > 0) {
    result.jitsiCustomDomains = jitsiDomains;
  }

  if (wire[FLAG_KEYS.isTtsEnabled]) {
    result.isTtsEnabled = true;
  }
  if (wire[FLAG_KEYS.isExternalServiceForTts]) {
    result.isExternalServiceForTts = true;
  }
  const ttsVoices = wire[FLAG_KEYS.ttsVoices];
  if (ttsVoices && ttsVoices.length > 0) {
    result.ttsVoices = ttsVoices;
  }

  const calendars = wire[FLAG_KEYS.enabledCalendars];
  if (calendars && calendars.length > 0) {
    result.enabledCalendars = calendars;
  }
  if (wire[FLAG_KEYS.isSSOVisible]) {
    result.isSSOVisible = true;
  }
  if (wire[FLAG_KEYS.isPasswordAuthDisabled]) {
    result.isPasswordAuthDisabled = true;
  }

  if (wire[FLAG_KEYS.isCallAssistantFeaturesEnabled]) {
    result.isCallAssistantFeaturesEnabled = true;
  }
  if (wire[FLAG_KEYS.isCallAssistantFeaturesEnabledFF]) {
    result.isCallAssistantFeaturesEnabledFF = true;
  }
  if (wire[FLAG_KEYS.isExternalIntegrationsProviderEnabled]) {
    result.isExternalIntegrationsProviderEnabled = true;
  }
  if (wire[FLAG_KEYS.isSamlSeatTiersEnabled]) {
    result.isSamlSeatTiersEnabled = true;
  }

  if (wire[FLAG_KEYS.isSiemExportEnabled]) {
    result.isSiemExportEnabled = true;
  }
  if (wire[FLAG_KEYS.isSiemGlobalCollectorActive]) {
    result.isSiemGlobalCollectorActive = true;
  }

  if (wire[FLAG_KEYS.isModelApprovalEnabled]) {
    result.isModelApprovalEnabled = true;
  }

  if (wire[FLAG_KEYS.isDomainVerificationUnavailable]) {
    result.isDomainVerificationUnavailable = true;
  }

  if (wire[FLAG_KEYS.isMobileChatSearchEnabled]) {
    result.isMobileChatSearchEnabled = true;
  }

  // NOTE: Bot install URLs carry a string payload, not a boolean —
  // translated as a non-empty assignment to mirror the conditional
  // inclusion done in to-wire-format.ts. Consumers treat the absence
  // of either field as "this bot isn't configured for the deployment".
  const supportEmail = wire[FLAG_KEYS.supportEmail];
  if (supportEmail) {
    result.supportEmail = supportEmail;
  }

  const slackBotUri = wire[FLAG_KEYS.slackBotUri];
  if (slackBotUri) {
    result.slackBotUri = slackBotUri;
  }
  const teamsBotUri = wire[FLAG_KEYS.teamsBotUri];
  if (teamsBotUri) {
    result.teamsBotUri = teamsBotUri;
  }

  // NOTE: NSAI-5934 — the chat-map tile server. Only a string URL and a
  // numeric zoom are taken: anything else on the wire is read as absent, which
  // the map answers by leaving the basemap off.
  const mapTileUrl = wire[FLAG_KEYS.mapTileUrl];
  if (typeof mapTileUrl === "string" && mapTileUrl.length > 0) {
    result.mapTileUrl = mapTileUrl;
  }
  const mapTileAttribution = wire[FLAG_KEYS.mapTileAttribution];
  if (typeof mapTileAttribution === "string" && mapTileAttribution.length > 0) {
    result.mapTileAttribution = mapTileAttribution;
  }
  const mapMaxZoom = wire[FLAG_KEYS.mapMaxZoom];
  if (typeof mapMaxZoom === "number") {
    result.mapMaxZoom = mapMaxZoom;
  }

  // NOTE: NSAI-5881 — validated rather than copied: the block comes from
  // whichever server the client points at, and a missing or unusable block
  // decodes to "absent" — the same answer as a server that predates it.
  const deployment = parseAppConfigDeployment(wire[FLAG_KEYS.deployment]);
  if (deployment) {
    result.deployment = deployment;
  }

  return result;
};
